import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'motion/react'

import {
  AdminJob,
  AdminJobScope,
  AdminJobsPage,
  JobStatus,
  cancelJob,
  listAdminJobs,
  retryJob,
} from '../api/client'
import ParseActivityPanel from '../components/checkin/ParseActivityPanel'
import { useParseActivity, type ParseActivity } from '../hooks/useParseActivity'

// Animation vocabulary (CLAUDE.md): surfaces pop with the 500/30 spring,
// icons/buttons use the 400/20 one.
const SURFACE_SPRING = { type: 'spring' as const, stiffness: 500, damping: 30 }
const ICON_SPRING = { type: 'spring' as const, stiffness: 400, damping: 20 }
const SURFACE_POP = {
  initial: { opacity: 0, scale: 0.95, y: -8 },
  animate: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.95, y: -8 },
  transition: SURFACE_SPRING,
  style: { transformOrigin: 'top center' },
}

const PAGE_SIZE = 50
const STATUSES: JobStatus[] = ['queued', 'running', 'ok', 'error', 'rate_limited', 'cancelled']
const LIVE: JobStatus[] = ['queued', 'running']
const isLive = (status: JobStatus) => LIVE.includes(status)

// The list re-reads itself: quickly while something is live, slowly otherwise
// so a job enqueued from elsewhere still turns up.
const LIVE_POLL_MS = 3000
const IDLE_POLL_MS = 15000
// A running job that is cancelled ends its watch at once, but its runner
// writes the record's last event at its next heartbeat (up to 10s later). The
// stored read is retried until that event lands, so the panel does not sit on
// a spinner that will never stop.
const SETTLE_POLL_MS = 2000
const SETTLE_TRIES = 10

const STATUS_STYLE: Record<JobStatus, string> = {
  queued: 'bg-gray-100 text-gray-700',
  running: 'bg-amber-100 text-amber-800',
  ok: 'bg-green-100 text-green-800',
  error: 'bg-rose-100 text-rose-800',
  rate_limited: 'bg-rose-50 text-rose-700',
  cancelled: 'bg-gray-200 text-gray-600',
}

const formatDuration = (ms: number | null): string => {
  if (ms == null) return '—'
  if (ms < 1000) return `${ms} ms`
  const s = ms / 1000
  if (s < 90) return `${s.toFixed(1)} s`
  const m = Math.floor(s / 60)
  return `${m}m ${Math.round(s % 60)}s`
}

const formatLease = (job: AdminJob): string => {
  if (job.lease_ms == null) return '—'
  if (job.lease_ms < 0) return 'lapsed'
  return `${Math.ceil(job.lease_ms / 1000)}s`
}

// The columns are DATETIME holding UTC, so the API hands back a naive ISO
// string; pin it to UTC before rendering it in the viewer's zone.
const formatTime = (iso: string | null): string => {
  if (!iso) return '—'
  const d = new Date(/[Zz]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

// The app's HTTPException handler renames `detail` to `error` (main.py), but
// FastAPI's own validation errors still come back under `detail`.
const apiMessage = (e: any, fallback: string): string =>
  e?.response?.data?.error || e?.response?.data?.detail || e?.message || fallback

interface Listing {
  jobs: AdminJob[]
  nextBefore: number | null
}

/**
 * A fresh first page over what is loaded: the page replaces every row from its
 * oldest up, and rows below it — later pages the reader loaded — stay. A row
 * inside the page's range that it no longer carries has left the filter.
 */
const mergeFirstPage = (prev: Listing, page: AdminJobsPage): Listing => {
  if (!page.jobs.length) return { jobs: [], nextBefore: null }
  const floor = page.jobs[page.jobs.length - 1].id
  const older = prev.jobs.filter((j) => j.id < floor)
  return {
    jobs: [...page.jobs, ...older],
    nextBefore: older.length ? prev.nextBefore : page.next_before_id,
  }
}

const Chevron: React.FC<{ open: boolean }> = ({ open }) => (
  <motion.span
    animate={{ rotate: open ? 90 : 0 }}
    transition={ICON_SPRING}
    className="inline-block text-gray-400 select-none"
  >
    ▸
  </motion.span>
)

const ActionButton: React.FC<{
  label: string
  disabled: boolean
  onClick: () => void
}> = ({ label, disabled, onClick }) => (
  <motion.button
    whileHover={{ scale: 1.1 }}
    whileTap={{ scale: 0.9 }}
    transition={ICON_SPRING}
    disabled={disabled}
    onClick={(e) => {
      e.stopPropagation()
      onClick()
    }}
    className="px-2.5 py-1 rounded-md text-xs border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-50"
  >
    {label}
  </motion.button>
)

const Fact: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="min-w-0">
    <div className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">{label}</div>
    <div className="font-mono text-xs text-gray-700 break-all">{children}</div>
  </div>
)

const Json: React.FC<{ value: unknown; tone: 'error' | 'plain' }> = ({ value, tone }) => (
  <pre
    className={`text-xs whitespace-pre-wrap break-words rounded border p-3 max-h-48 overflow-y-auto ${
      tone === 'error'
        ? 'border-rose-200 bg-rose-50 text-rose-800'
        : 'border-gray-200 bg-white text-gray-700'
    }`}
  >
    {JSON.stringify(value, null, 2)}
  </pre>
)

const JobDetail: React.FC<{
  job: AdminJob
  activity?: ParseActivity
  loading: boolean
  onToggleExpanded: (expanded: boolean) => void
  onToggleTechnical: (technical: boolean) => void
}> = ({ job, activity, loading, onToggleExpanded, onToggleTechnical }) => (
  <div className="flex flex-col gap-3">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <Fact label="Job">{job.job_id}</Fact>
      <Fact label="Subject">
        {job.subject_type ? `${job.subject_type} ${job.subject_id ?? ''}` : '—'}
      </Fact>
      <Fact label="Dedupe key">{job.dedupe_key ?? '—'}</Fact>
      <Fact label="Lease owner">{job.lease_owner ?? '—'}</Fact>
      <Fact label="Created">{formatTime(job.created_at)}</Fact>
      <Fact label="Started">{formatTime(job.started_at)}</Fact>
      <Fact label="Finished">{formatTime(job.finished_at)}</Fact>
      <Fact label="Root LLM log">{job.root_llm_log_id ?? '—'}</Fact>
    </div>
    {job.error && <Json value={{ error_code: job.error_code, ...job.error }} tone="error" />}
    {job.result && <Json value={job.result} tone="plain" />}
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2">
      {activity ? (
        <ParseActivityPanel
          activity={activity}
          idleLabel={job.status === 'queued' ? 'Queued' : 'Running'}
          onToggleExpanded={onToggleExpanded}
          onToggleTechnical={onToggleTechnical}
        />
      ) : (
        <div className="text-xs text-gray-400">{loading ? 'Loading…' : 'No events yet.'}</div>
      )}
    </div>
  </div>
)

const JobsPage: React.FC = () => {
  const [listing, setListing] = useState<Listing>({ jobs: [], nextBefore: null })
  // null is "this server's own scope", which the first page names.
  const [scope, setScope] = useState<string | null>(null)
  const [currentScope, setCurrentScope] = useState('')
  const [scopes, setScopes] = useState<AdminJobScope[]>([])
  const [status, setStatus] = useState<JobStatus | null>(null)
  const [kind, setKind] = useState<string | null>(null)
  const [kinds, setKinds] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [openIds, setOpenIds] = useState<ReadonlySet<number>>(new Set())
  const [loadingIds, setLoadingIds] = useState<ReadonlySet<number>>(new Set())
  const [busyIds, setBusyIds] = useState<ReadonlySet<number>>(new Set())

  // Records are keyed by the job row's id: the hook's keys are numbers, and a
  // job with no check-in behind it needs one of its own.
  const { activities, watchJob, reviewJob, setExpanded, setTechnical } = useParseActivity()

  // Not state: nothing renders from these.
  const watching = useRef<Set<number>>(new Set())
  const timers = useRef<Set<number>>(new Set())
  const mounted = useRef(true)
  const openRef = useRef(openIds)
  openRef.current = openIds

  useEffect(() => {
    mounted.current = true
    const pending = timers.current
    return () => {
      mounted.current = false
      for (const t of pending) window.clearTimeout(t)
      pending.clear()
    }
  }, [])

  const mark = (
    set: React.Dispatch<React.SetStateAction<ReadonlySet<number>>>,
    id: number,
    on: boolean,
  ) =>
    set((prev) => {
      if (prev.has(id) === on) return prev
      const next = new Set(prev)
      if (on) next.add(id)
      else next.delete(id)
      return next
    })

  const fetchPage = useCallback(
    (beforeId?: number) =>
      listAdminJobs({
        scope: scope ?? undefined,
        status: status ? [status] : undefined,
        kind: kind ?? undefined,
        beforeId,
        limit: PAGE_SIZE,
      }),
    [scope, status, kind],
  )

  const absorb = useCallback((page: AdminJobsPage) => {
    setCurrentScope(page.current_scope)
    setScopes(page.scopes)
    setKinds((prev) => {
      const seen = new Set(prev)
      const added = page.jobs.map((j) => j.kind).filter((k) => !seen.has(k) && seen.add(k))
      return added.length ? [...prev, ...added].sort() : prev
    })
  }, [])

  // A filter change starts over from the first page.
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setOpenIds(new Set())
    fetchPage()
      .then((page) => {
        if (cancelled) return
        absorb(page)
        setListing({ jobs: page.jobs, nextBefore: page.next_before_id })
      })
      .catch((e) => {
        if (cancelled) return
        setError(
          e?.response?.status === 403
            ? 'This account is not an admin.'
            : apiMessage(e, 'Failed to load'),
        )
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [fetchPage, absorb])

  const refresh = useCallback(async () => {
    try {
      const page = await fetchPage()
      if (!mounted.current) return
      absorb(page)
      setListing((prev) => mergeFirstPage(prev, page))
    } catch (e) {
      if (mounted.current) setError(apiMessage(e, 'Failed to refresh'))
    }
  }, [fetchPage, absorb])

  const anyLive = listing.jobs.some((j) => isLive(j.status))
  useEffect(() => {
    const timer = window.setInterval(() => void refresh(), anyLive ? LIVE_POLL_MS : IDLE_POLL_MS)
    return () => window.clearInterval(timer)
  }, [anyLive, refresh])

  const loadMore = async () => {
    if (listing.nextBefore == null) return
    try {
      const page = await fetchPage(listing.nextBefore)
      absorb(page)
      setListing((prev) => ({
        jobs: [...prev.jobs, ...page.jobs.filter((j) => j.id < (prev.nextBefore ?? Infinity))],
        nextBefore: page.next_before_id,
      }))
    } catch (e) {
      setError(apiMessage(e, 'Failed to load more'))
    }
  }

  /** The stored record, read again until it closes or the row is shut. */
  const settle = useCallback(
    (job: AdminJob, tries = 0) => {
      mark(setLoadingIds, job.id, true)
      reviewJob(job.id, job.job_id, job.runner_scope)
        .then((stored) => {
          if (!stored || !mounted.current) return
          const recordOpen = stored.status === 'running' && !isLive(stored.job_status)
          if (recordOpen && tries < SETTLE_TRIES && openRef.current.has(job.id)) {
            const t = window.setTimeout(() => {
              timers.current.delete(t)
              settle(job, tries + 1)
            }, SETTLE_POLL_MS)
            timers.current.add(t)
          }
        })
        .catch((e) => {
          if (mounted.current) setError(apiMessage(e, 'Failed to load the job record'))
        })
        .finally(() => {
          if (mounted.current) mark(setLoadingIds, job.id, false)
        })
    },
    [reviewJob],
  )

  /** A live job is watched; a finished one is read from the store. */
  const loadRecord = useCallback(
    (job: AdminJob) => {
      if (watching.current.has(job.id)) return
      if (!isLive(job.status)) {
        settle(job)
        return
      }
      watching.current.add(job.id)
      watchJob(job.id, job.job_id, job.runner_scope)
        .then(
          () => undefined,
          // Cancelled, rate limited, failed or dropped: the watch ends early
          // and its record may not be closed yet, so read the store instead.
          () => {
            if (mounted.current && openRef.current.has(job.id)) settle(job)
          },
        )
        .finally(() => {
          watching.current.delete(job.id)
          if (mounted.current) void refresh()
        })
      setExpanded(job.id, true)
    },
    [watchJob, settle, setExpanded, refresh],
  )

  const toggle = (job: AdminJob) => {
    const opening = !openIds.has(job.id)
    mark(setOpenIds, job.id, opening)
    if (opening) loadRecord(job)
  }

  const act = async (job: AdminJob, action: 'cancel' | 'retry') => {
    mark(setBusyIds, job.id, true)
    setError(null)
    try {
      if (action === 'cancel') {
        await cancelJob(job.job_id, job.runner_scope)
      } else {
        await retryJob(job.job_id, job.runner_scope)
        if (openRef.current.has(job.id)) loadRecord({ ...job, status: 'queued' })
      }
      await refresh()
    } catch (e) {
      setError(apiMessage(e, `Failed to ${action}`))
    } finally {
      mark(setBusyIds, job.id, false)
    }
  }

  const shownScope = scope ?? currentScope
  const scopeOptions = scopes.some((s) => s.runner_scope === currentScope)
    ? scopes
    : [{ runner_scope: currentScope, jobs: 0, live: 0 }, ...scopes]

  return (
    <div className="flex flex-col min-h-screen bg-blush-light">
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-wrap sm:flex-nowrap items-start justify-between gap-4 mb-6">
          <div className="min-w-0">
            <Link to="/admin" className="text-sm text-amber-800 hover:underline">
              ← Admin
            </Link>
            <h1 className="text-3xl font-bold text-gray-800 mt-1">Jobs</h1>
            <p className="text-sm text-gray-500 mt-1">
              Every user's jobs in one <span className="font-mono">runner_scope</span>, newest
              first.
            </p>
          </div>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            transition={ICON_SPRING}
            onClick={() => void refresh()}
            className="px-3 py-1.5 rounded-md text-sm whitespace-nowrap bg-secondary text-gray-700 hover:bg-secondary-dark"
          >
            Refresh
          </motion.button>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3">
            {error}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 mb-6">
          {[null, ...STATUSES].map((s) => (
            <motion.button
              key={s ?? 'all'}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              transition={ICON_SPRING}
              onClick={() => setStatus(s)}
              className={`px-3 py-1.5 rounded-md text-sm border ${s ? 'font-mono' : ''} ${
                status === s
                  ? 'bg-action text-plum border-amber-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
              }`}
            >
              {s ?? 'All'}
            </motion.button>
          ))}
          <select
            aria-label="Scope"
            value={shownScope}
            onChange={(e) => setScope(e.target.value === currentScope ? null : e.target.value)}
            className="ml-2 px-2 py-1.5 rounded-md text-sm border border-gray-300 bg-white text-gray-700 font-mono"
          >
            {scopeOptions.map((s) => (
              <option key={s.runner_scope} value={s.runner_scope}>
                {s.runner_scope}
                {s.runner_scope === currentScope ? ' (this server)' : ''}
                {s.live ? ` · ${s.live} live` : ''}
              </option>
            ))}
          </select>
          <select
            aria-label="Kind"
            value={kind ?? ''}
            onChange={(e) => setKind(e.target.value || null)}
            className="px-2 py-1.5 rounded-md text-sm border border-gray-300 bg-white text-gray-700 font-mono"
          >
            <option value="">All kinds</option>
            {kinds.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-600">
                <tr>
                  <th className="px-2 py-2 w-6"></th>
                  <th className="px-3 py-2 font-medium">Kind</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">User</th>
                  <th className="px-3 py-2 font-medium">Scope</th>
                  <th className="px-3 py-2 font-medium text-right">Attempts</th>
                  <th className="px-3 py-2 font-medium">Created</th>
                  <th className="px-3 py-2 font-medium text-right">Duration</th>
                  <th className="px-3 py-2 font-medium text-right">Lease</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {listing.jobs.map((job) => {
                  const open = openIds.has(job.id)
                  const busy = busyIds.has(job.id)
                  return (
                    <React.Fragment key={job.id}>
                      <tr
                        data-testid={`job-row-${job.job_id}`}
                        onClick={() => toggle(job)}
                        className={`border-t border-gray-100 cursor-pointer ${
                          open ? 'bg-amber-50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <td className="px-2 py-2 text-center">
                          <Chevron open={open} />
                        </td>
                        <td className="px-3 py-2">
                          <span className="font-mono text-gray-800">{job.kind}</span>
                          <span className="ml-2 font-mono text-xs text-gray-400">
                            {job.job_id.slice(0, 8)}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <span
                            title={job.error_code ?? undefined}
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_STYLE[job.status]}`}
                          >
                            {job.status}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <Link
                            to={`/admin/users/${job.user_id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="font-mono text-xs text-amber-800 hover:underline"
                          >
                            #{job.user_id}
                          </Link>
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-gray-500">
                          {job.runner_scope}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                          {job.attempts}/{job.max_attempts}
                        </td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {formatTime(job.created_at)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-600 whitespace-nowrap">
                          {formatDuration(job.duration_ms)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-500 whitespace-nowrap">
                          {formatLease(job)}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          {isLive(job.status) ? (
                            <ActionButton
                              label="Cancel"
                              disabled={busy}
                              onClick={() => void act(job, 'cancel')}
                            />
                          ) : (
                            <ActionButton
                              label="Retry"
                              disabled={busy}
                              onClick={() => void act(job, 'retry')}
                            />
                          )}
                        </td>
                      </tr>
                      {/* A `<tr>` can't be a motion child without breaking
                          table layout, so the cell's content pops instead. */}
                      {open && (
                        <tr className="border-t border-gray-100 bg-gray-50">
                          <td></td>
                          <td colSpan={9} className="px-4 py-4">
                            <motion.div {...SURFACE_POP}>
                              <JobDetail
                                job={job}
                                activity={activities[job.id]}
                                loading={loadingIds.has(job.id)}
                                onToggleExpanded={(expanded) => setExpanded(job.id, expanded)}
                                onToggleTechnical={(technical) =>
                                  setTechnical(job.id, technical)
                                }
                              />
                            </motion.div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })}
                {listing.jobs.length === 0 && (
                  <tr>
                    <td colSpan={10} className="px-6 py-6 text-center text-gray-500">
                      {loading ? 'Loading…' : 'No jobs match this filter.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {listing.nextBefore != null && (
            <div className="px-6 py-3 border-t border-gray-100 text-center">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                transition={ICON_SPRING}
                onClick={() => void loadMore()}
                className="px-3 py-1.5 rounded-md text-sm border border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
              >
                Older
              </motion.button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default JobsPage
